import type { PropertyRecord } from "../types/portfolio";
import type { InvestorProfile } from "../types/entities";

export interface TemplateContext {
  property: PropertyRecord;
  profile: InvestorProfile;
  recipientName: string;
}

export interface EmailDraft {
  subject: string;
  body: string;
}

export type TemplateFn = (ctx: TemplateContext) => EmailDraft;

function fmtDate(iso: string | undefined | null): string {
  if (!iso) return "[date not set]";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function userName(p: InvestorProfile): string {
  return p.preferredName ?? p.fullLegalName ?? "[YOUR NAME]";
}

export const EMAIL_TEMPLATES: Record<string, TemplateFn> = {
  "rent-review-pm": (ctx) => ({
    subject: `Rent review — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nI'd like to initiate a rent review for ${ctx.property.address}.\n\nCurrent details:\n• Current rent: $${ctx.property.weeklyRent ?? "[RENT]"}/week\n• Lease started: ${fmtDate(ctx.property.leaseStartDate)}\n• Lease ends: ${fmtDate(ctx.property.leaseEndDate)}\n\nCould you please prepare a recommendation including comparable market rents in the area?\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "lease-expiry-pm": (ctx) => ({
    subject: `Lease expiring — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nThe lease at ${ctx.property.address} expires on ${fmtDate(ctx.property.leaseEndDate)}.\n\nCould you please:\n• Confirm tenant intentions (renewing or vacating)\n• Begin marketing if vacating\n• Recommend any rent adjustment if renewing\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "insurance-renewal-pm": (ctx) => ({
    subject: `Insurance renewal due — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nThe landlord insurance for ${ctx.property.address} is approaching renewal.\n\nCould you please:\n• Confirm whether you handle renewal or it's my responsibility\n• Send through the renewal quote when received\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "smoke-alarm-pm": (ctx) => ({
    subject: `Smoke alarm certification — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nThe smoke alarm certification for ${ctx.property.address} is approaching expiry (${fmtDate(ctx.property.smokeAlarmExpiryDate)}).\n\nCould you please arrange the inspection/certification renewal and send through the invoice?\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "pool-safety-pm": (ctx) => ({
    subject: `Pool safety certificate — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nThe pool safety certificate for ${ctx.property.address} expires on ${fmtDate(ctx.property.poolSafetyCertDate)}.\n\nCould you arrange a renewal inspection and send through the invoice?\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "io-expiry-broker": (ctx) => ({
    subject: `IO period ending — refinance discussion — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nThe interest-only period on my loan for ${ctx.property.address} is approaching expiry.\n\nLoan details:\n• Current balance: $${(ctx.property.loanBalance ?? 0).toLocaleString()}\n• Current rate: ${ctx.property.rate ?? 0}%\n• IO term: ${ctx.property.ioTermYears ?? 0} years\n• Loan started: ${fmtDate(ctx.property.loanStartDate)}\n\nI'd like to discuss:\n• Extending the IO period vs converting to P&I\n• Refinancing options\n• Current market rates\n\nWhen would suit for a call?\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "fixed-rate-broker": (ctx) => ({
    subject: `Fixed rate expiring — rate review — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nThe fixed rate on my loan for ${ctx.property.address} expires on ${fmtDate(ctx.property.fixedRateExpiryDate)}.\n\nLoan details:\n• Current balance: $${(ctx.property.loanBalance ?? 0).toLocaleString()}\n• Current rate: ${ctx.property.rate ?? 0}%\n\nCould you provide:\n• Current best fixed rates (1/2/3/5 yr)\n��� Variable rate comparison\n• Refinance options if rates are materially better elsewhere\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "valuation-broker": (ctx) => ({
    subject: `Bank valuation request — ${ctx.property.address}`,
    body: `Hi ${ctx.recipientName},\n\nMy last bank valuation for ${ctx.property.address} was ${fmtDate(ctx.property.lastValuationDate)} (over 2 years old).\n\nCould you arrange a fresh bank valuation? Particularly interested in:\n• Current bank-recognised value\n• Whether revised value would unlock equity for further investment\n\nLast valuation: $${(ctx.property.bankValuation ?? 0).toLocaleString()}\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "trust-deadline-accountant": (ctx) => ({
    subject: `Trust resolution — 31 August deadline`,
    body: `Hi ${ctx.recipientName},\n\nThe 31 August trustee resolution deadline is approaching.\n\nCould you please:\n• Prepare distribution resolutions for my trust(s)\n• Confirm any planning around current year income/losses\n• Send through documents for signing\n\nThanks,\n${userName(ctx.profile)}`,
  }),

  "equity-available-buyer-agent": (ctx) => ({
    subject: `Equity available — investment property search`,
    body: `Hi ${ctx.recipientName},\n\nI have usable equity available for further property investment.\n\nKey parameters:\n• [Add target suburb/region]\n• [Add price range]\n• [Add property type preference]\n\nCould we set up a call to discuss current opportunities?\n\nThanks,\n${userName(ctx.profile)}`,
  }),
};
